// display friendly error page
import * as React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardSubtitle,
} from "@progress/kendo-react-layout";
import { StackLayout } from "@progress/kendo-react-layout";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import { useSearchParams } from "react-router-dom";
import { CPText } from "../../../shared/components/base";
import { baseURL } from "shared/config";

const SUPPORT_EMAIL = "helpdesk@greatwide-tm.com";

interface ErrorVariant {
  icon: string;
  title: string;
  subtitle: string;
  steps: string[];
}

const VARIANTS: Record<string, ErrorVariant> = {
  account_inactive: {
    icon: "fa-solid fa-user-slash",
    title: "Account Deactivated",
    subtitle: "Your account has been deactivated and you can no longer sign in.",
    steps: [
      "Please contact your manager or administrator if you believe this is a mistake.",
      `Reach out to support at ${SUPPORT_EMAIL} for further assistance.`,
    ],
  },
  sso_pending: {
    icon: "fa-solid fa-clock",
    title: "Account Pending Review",
    subtitle:
      "Your access request has been received and is currently being reviewed by an administrator.",
    steps: [
      "Once your account is set up, you will receive a confirmation email.",
      "Please try signing in again after you receive that email.",
      "If you have not heard back within a reasonable time, contact your manager.",
      `You can also reach support at ${SUPPORT_EMAIL} if you need assistance.`,
    ],
  },
  access_denied: {
    icon: "fa-solid fa-ban",
    title: "Access Not Approved",
    subtitle: "Your access request was not approved.",
    steps: [
      "Please contact your manager if you believe you should have access.",
      `Reach out to support at ${SUPPORT_EMAIL} if you need further assistance.`,
    ],
  },
  auth_failed: {
    icon: "fa-solid fa-shield-halved",
    title: "Sign-In Failed",
    subtitle:
      "We were unable to complete your sign-in with Microsoft. This can happen if the request timed out or was cancelled.",
    steps: [
      "Close this tab and try signing in again.",
      "Make sure you are using your company Microsoft account.",
      "Clear your browser cookies and retry.",
      `If the problem continues, contact support at ${SUPPORT_EMAIL}.`,
    ],
  },
  missing_identifier: {
    icon: "fa-solid fa-id-card",
    title: "Account Not Recognised",
    subtitle:
      "Your identity could not be confirmed. Your account may not be set up in this application yet.",
    steps: [
      "Use the Request Access link on the login page to submit an access request.",
      "Check with your manager that your account has been provisioned.",
      `Contact support at ${SUPPORT_EMAIL} if you believe this is a mistake.`,
    ],
  },
  app_error: {
    icon: "fa-solid fa-triangle-exclamation",
    title: "Something Went Wrong",
    subtitle: "An unexpected error occurred in the application.",
    steps: [
      "Refresh the page — this resolves most issues.",
      "If the page does not load, clear your browser cache and try again.",
      "Navigate back to the home screen using the button below.",
      `If the problem persists, contact support at ${SUPPORT_EMAIL} with the time it occurred.`,
    ],
  },
};

const DEFAULT_VARIANT = VARIANTS.app_error;

interface ErrorPageProps {
  errorCode?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ errorCode }) => {
  const [searchParams] = useSearchParams();
  const code = errorCode ?? searchParams.get("error") ?? "app_error";
  const variant = VARIANTS[code] ?? DEFAULT_VARIANT;

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleRetryLogin = () => {
    window.location.href = `${baseURL}/api/bihub/auth/saml-login`;
  };

  const isAuthError = code === "auth_failed" || code === "missing_identifier";

  return (
    <Card className="forgot-password-form-card" style={{ maxWidth: 520 }}>
      <CardHeader>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 12,
          }}
        >
          <CPFontAwesome icon={variant.icon} size={40} />
        </div>
        <CardTitle>
          <CPText textStyle="h5" align="center" style={{ flex: 1 }}>
            {variant.title}
          </CPText>
        </CardTitle>
        <CardSubtitle>
          <CPText align="center" textStyle="subtitle">
            {variant.subtitle}
          </CPText>
        </CardSubtitle>
      </CardHeader>
      <CardBody>
        <StackLayout
          orientation="vertical"
          gap={8}
          style={{ marginBottom: 20 }}
        >
          {variant.steps.map((step, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
            >
              <CPFontAwesome
                icon="fa-solid fa-circle-dot"
                size={14}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <CPText textStyle="body">{step}</CPText>
            </div>
          ))}
        </StackLayout>

        <StackLayout orientation="vertical" gap={10}>
          {isAuthError && (
            <CPButton
              themeColor="primary"
              style={{ width: "100%" }}
              onClick={handleRetryLogin}
            >
              <CPFontAwesome icon="fa-solid fa-rotate-right" size={14} inline />
              Try Signing In Again
            </CPButton>
          )}
          <CPButton
            fillMode="flat"
            themeColor="dark"
            style={{ width: "100%" }}
            onClick={handleGoHome}
          >
            <CPFontAwesome icon="fa-solid fa-house" size={14} inline />
            Go to Home Screen
          </CPButton>
        </StackLayout>
      </CardBody>
    </Card>
  );
};

export default ErrorPage;
